import { useEffect, useState } from "react";
import { Close } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { useAuth } from "../../hooks/AuthProvider";
import {
  AI_PROVIDER_CONNECT_ENDPOINT,
  AI_PROVIDER_ENDPOINT,
  AI_PROVIDER_VERIFY_ENDPOINT,
} from "../../config/urls";
import { aiFetch, errorMessage, providerIcon } from "./aiApi";

const modalSx = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  maxWidth: "calc(100vw - 32px)",
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

/**
 * Connect / re-verify / replace / disconnect one AI provider with the tenant's own key.
 * The key is verified with the provider before it is saved, and only a masked hint is
 * ever shown back.
 */
export default function AIProviderModal({ provider, open, onClose, onChanged }) {
  const { getValidToken } = useAuth();
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [replacing, setReplacing] = useState(false);
  const [affected, setAffected] = useState(null);

  useEffect(() => {
    if (open) {
      setApiKey("");
      setError("");
      setReplacing(false);
      setAffected(null);
    }
  }, [open, provider?.slug]);

  if (!provider) return null;
  const connection = provider.connection;
  const showKeyForm = !connection || replacing;

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  const connect = () =>
    run(async () => {
      const res = await aiFetch(getValidToken, AI_PROVIDER_CONNECT_ENDPOINT(provider.slug), {
        method: "POST",
        body: { api_key: apiKey.trim() },
      });
      if (!res.ok) {
        setError(errorMessage(res.data));
        return;
      }
      setApiKey("");
      setReplacing(false);
      onChanged(res.data);
    });

  const verify = () =>
    run(async () => {
      const res = await aiFetch(getValidToken, AI_PROVIDER_VERIFY_ENDPOINT(provider.slug), { method: "POST" });
      if (!res.ok) {
        setError(errorMessage(res.data));
        if (res.data?.provider) onChanged(res.data.provider);
        return;
      }
      onChanged(res.data);
    });

  const previewDisconnect = () =>
    run(async () => {
      const res = await aiFetch(getValidToken, `${AI_PROVIDER_ENDPOINT(provider.slug)}?dry_run=1`, {
        method: "DELETE",
      });
      if (!res.ok) {
        setError(errorMessage(res.data));
        return;
      }
      setAffected(res.data.affected || []);
    });

  const disconnect = () =>
    run(async () => {
      const res = await aiFetch(getValidToken, AI_PROVIDER_ENDPOINT(provider.slug), { method: "DELETE" });
      if (!res.ok) {
        setError(errorMessage(res.data));
        return;
      }
      setAffected(null);
      onChanged({ ...provider, connection: null });
    });

  const chatModels = (connection?.accessible_models || []).filter((m) => m.supports_chat);
  const embedModels = (connection?.accessible_models || []).filter((m) => m.supports_embed);

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalSx}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6">
            {providerIcon(provider.slug)} {connection ? `${provider.name} connected` : `Connect ${provider.name}`}
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Divider />

        {connection?.status === "error" && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {connection.last_error?.message || `Your ${provider.name} key is not working.`}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          {connection ? (
            <Typography variant="body2">
              Key <strong>{connection.key_hint}</strong>
              {connection.verified_at && <> · verified {new Date(connection.verified_at).toLocaleString()}</>}
              . Choose it with “My own key” in Agentic AI → Configuration or on a
              product discovery node.
            </Typography>
          ) : (
            <Typography variant="body2">
              Use your own {provider.name} API key for AI replies, so usage is billed to your {provider.name}{" "}
              account. The key is checked with {provider.name} before it is saved, stored encrypted, and never
              shown again.
              {provider.docs_url && (
                <>
                  {" "}
                  <Link href={provider.docs_url} target="_blank" rel="noreferrer">
                    Get an API key
                  </Link>
                </>
              )}
            </Typography>
          )}
        </Box>

        {connection && chatModels.length + embedModels.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Models available with this key
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {chatModels.map((m) => (
                <Chip key={m.model_id} size="small" label={m.display_name} />
              ))}
              {embedModels.map((m) => (
                <Chip key={m.model_id} size="small" variant="outlined" label={`${m.display_name} (embeddings)`} />
              ))}
            </Box>
          </Box>
        )}

        {showKeyForm && (
          <TextField
            sx={{ mt: 3 }}
            fullWidth
            type="password"
            autoComplete="off"
            label={`${provider.name} API key`}
            placeholder={provider.key_hint_prefix ? `${provider.key_hint_prefix}...` : ""}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
        )}

        {affected && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {affected.length === 0 ? (
              <>Nothing uses {provider.name} with your own key. Disconnect it?</>
            ) : (
              <>
                These use {provider.name} with your own key and will switch to BotBridge keys (if fallback is on) or
                stop getting AI replies:
                <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
                  {affected.map((a, i) => (
                    <li key={i}>{a.label}</li>
                  ))}
                </ul>
              </>
            )}
          </Alert>
        )}

        {error && (
          <Typography variant="caption" sx={{ color: "error.main", display: "block", mt: 2 }} role="alert">
            {error}
          </Typography>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
          {busy && <CircularProgress size={24} sx={{ alignSelf: "center", mr: 1 }} />}
          {affected ? (
            <>
              <Button onClick={() => setAffected(null)} disabled={busy}>
                Cancel
              </Button>
              <Button variant="contained" color="error" onClick={disconnect} disabled={busy}>
                Disconnect
              </Button>
            </>
          ) : showKeyForm ? (
            <>
              {replacing && (
                <Button onClick={() => setReplacing(false)} disabled={busy}>
                  Cancel
                </Button>
              )}
              <Button variant="contained" onClick={connect} disabled={busy || !apiKey.trim()}>
                {replacing ? "Replace key" : "Connect"}
              </Button>
            </>
          ) : (
            <>
              <Button color="error" onClick={previewDisconnect} disabled={busy}>
                Disconnect
              </Button>
              <Button onClick={() => setReplacing(true)} disabled={busy}>
                Replace key
              </Button>
              <Button variant="contained" onClick={verify} disabled={busy}>
                Re-verify
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
}
